import { useTranslation } from 'react-i18next';
import { Box, IconButton, useTheme } from '@mui/material';
import { IconSparkles, IconX } from '@tabler/icons-react';

import { Typography } from '@zendesk/forethought-elements';
import type { ToastTone } from './ToastContext';

interface AIStudioToastCardProps {
  brand?: string;
  label?: string;
  preview: string;
  tone?: ToastTone;
  onClick?: () => void;
  onClose: () => void;
}

const AIStudioToastCard = ({
  brand = 'AI Studio',
  label,
  onClick,
  onClose,
  preview,
  tone = 'success',
}: AIStudioToastCardProps) => {
  const { t } = useTranslation('common');
  const { palette } = useTheme();
  const clickable = Boolean(onClick);
  const accent = tone === 'error' ? palette.colors.red[500] : '#17B790';
  const defaultLabelByTone: Record<ToastTone, string> = {
    error: t('aiStudioToastCard.defaultLabelError'),
    success: t('aiStudioToastCard.defaultLabelSuccess'),
  };
  const resolvedLabel = label ?? defaultLabelByTone[tone];

  return (
    <Box
      aria-label={t('aiStudioToastCard.ariaLabel', {
        brand,
        label: resolvedLabel,
      })}
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      sx={{
        alignItems: 'stretch',
        backdropFilter: 'blur(50px)',
        background: 'rgba(255, 255, 255, 0.65)',
        border: '1px solid rgba(255, 255, 255, 0.8)',
        borderRadius: '8px',
        boxShadow: '0 0 30px 0 rgba(0, 0, 0, 0.08)',
        cursor: clickable ? 'pointer' : 'default',
        display: 'flex',
        minHeight: '72px',
        overflow: 'clip',
        pointerEvents: 'auto',
        position: 'relative',
        WebkitBackdropFilter: 'blur(50px)',
        width: '470px',
      }}
    >
      <Box
        sx={{
          background: accent,
          flex: '0 0 10px',
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          gap: '5px',
          minWidth: 0,
          padding: '14px 40px 14px 16px',
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: '4px',
          }}
        >
          <IconSparkles color={palette.colors.black} size={19} />
          <Typography color={palette.colors.black} variant='font12Medium'>
            {brand}
          </Typography>
          <Box
            aria-hidden
            sx={{
              background: palette.colors.grey[400],
              borderRadius: '50%',
              height: '4px',
              mx: '2px',
              width: '4px',
            }}
          />
          <Typography color={palette.colors.grey[700]} variant='font12Medium'>
            {resolvedLabel}
          </Typography>
        </Box>
        <Typography color={palette.colors.black} noWrap variant='font14Medium'>
          {preview}
        </Typography>
      </Box>
      <IconButton
        aria-label={t('aiStudioToastCard.dismissAriaLabel')}
        onClick={event => {
          event.stopPropagation();
          onClose();
        }}
        size='small'
        sx={{
          color: palette.colors.grey[700],
          position: 'absolute',
          right: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
        }}
      >
        <IconX size={16} />
      </IconButton>
    </Box>
  );
};

export default AIStudioToastCard;
